import { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface CustomPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (label: string, prompt: string, availableIn: string) => void;
  editingPrompt?: { id: string; label: string; prompt: string; available_in?: string } | null;
}

export function CustomPromptModal({
  open,
  onOpenChange,
  onSave,
  editingPrompt
}: CustomPromptModalProps) {
  const [label, setLabel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [availableIn, setAvailableIn] = useState<string>('both');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when modal opens/closes or editing changes
  useEffect(() => {
    if (open) {
      if (editingPrompt) {
        setLabel(editingPrompt.label);
        setPrompt(editingPrompt.prompt);
        setAvailableIn(editingPrompt.available_in || 'both');
      } else {
        setLabel('');
        setPrompt('');
        setAvailableIn('both');
      }
    }
  }, [open, editingPrompt]);

  const handleSubmit = async () => {
    if (!label.trim() || !prompt.trim()) return;

    setIsSaving(true);
    try {
      await onSave(label.trim(), prompt.trim(), availableIn);
      setLabel('');
      setPrompt('');
      setAvailableIn('both');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save prompt:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setLabel('');
      setPrompt('');
      setAvailableIn('both');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingPrompt ? 'Edit Custom Prompt' : 'Create Custom Prompt'}
          </DialogTitle>
          <DialogDescription>
            Create a custom prompt that will appear in your Quick Prompts menu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="prompt-label">Prompt Label</Label>
            <Input
              id="prompt-label"
              placeholder="e.g., Detailed Patent Analysis"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={isSaving}
              maxLength={50}
            />
            <p className="text-xs text-gray-500">
              A short name for your prompt (max 50 characters)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-text">Prompt Text</Label>
            <Textarea
              id="prompt-text"
              placeholder="Enter the prompt text that will be used when you select this quick prompt..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[200px] resize-none"
              disabled={isSaving}
              maxLength={2000}
            />
            <p className="text-xs text-gray-500">
              The actual prompt text (max 2000 characters). You can use @ to reference documents.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="available-in">Available In</Label>
            <Select value={availableIn} onValueChange={setAvailableIn} disabled={isSaving}>
              <SelectTrigger id="available-in">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both Chat & Document Q&A</SelectItem>
                <SelectItem value="chat">Chat Only</SelectItem>
                <SelectItem value="document">Document Q&A Only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Choose where this prompt will be available
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!label.trim() || !prompt.trim() || isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                {editingPrompt ? 'Update Prompt' : 'Create Prompt'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
